import { useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Plus, X, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ImageUploader({ images, setImages, max = 12 }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handleFiles = async (files) => {
        if (!files?.length) return;
        if (images.length + files.length > max) {
            toast.error(`Maximum ${max} images per project`);
            return;
        }
        setUploading(true);
        const next = [...images];
        for (const file of files) {
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                next.push(data.path);
                setImages([...next]);
            } catch (err) {
                toast.error(`Failed to upload ${file.name}`);
            }
        }
        setUploading(false);
        if (inputRef.current) inputRef.current.value = "";
    };

    const remove = (path) => {
        setImages(images.filter((p) => p !== path));
    };

    return (
        <div data-testid="image-uploader">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {images.map((path) => (
                    <div key={path} className="relative aspect-[4/3] bg-neutral-100 group border border-neutral-200">
                        <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" />
                        <button
                            type="button"
                            data-testid={`remove-image-${path}`}
                            onClick={() => remove(path)}
                            className="absolute top-2 right-2 bg-black text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove image"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    </div>
                ))}
                {images.length < max && (
                    <button
                        type="button"
                        data-testid="add-image-btn"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="aspect-[4/3] border-2 border-dashed border-neutral-300 hover:border-black bg-neutral-50 hover:bg-white flex flex-col items-center justify-center gap-2 transition-colors text-neutral-600"
                    >
                        {uploading ? (
                            <>
                                <Spinner size={24} className="animate-spin" />
                                <span className="text-xs font-medium">Uploading…</span>
                            </>
                        ) : (
                            <>
                                <Plus size={24} weight="bold" />
                                <span className="text-xs font-medium">Add photo</span>
                            </>
                        )}
                    </button>
                )}
            </div>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                data-testid="image-file-input"
                onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            />
            <p className="text-xs text-neutral-500 mt-3">
                {images.length}/{max} photos · JPG, PNG, WEBP, GIF · max 15MB each
            </p>
        </div>
    );
}
